import React, { useEffect, useState, useRef } from "react";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Mic, Send, LogOut, CheckCircle2, Clock, Factory, Fuel, Boxes, UserCheck } from "lucide-react";

const TYPE_ICON = {
  production: Factory,
  material: Boxes,
  genset: Fuel,
  attendance: UserCheck,
};

const TYPE_LABEL = {
  production: "Production / उत्पादन",
  material: "Material / कच्चा माल",
  genset: "Genset / जनरेटर",
  attendance: "Attendance / हाजिरी",
};

export default function WorkerHome() {
  const { user, logout } = useAuth();
  const [text, setText] = useState("");
  const [parsed, setParsed] = useState(null);
  const [busy, setBusy] = useState(false);
  const [listening, setListening] = useState(false);
  const [recent, setRecent] = useState([]);
  const [attToday, setAttToday] = useState(null);
  const recogRef = useRef(null);

  useEffect(() => { refreshRecent(); }, []);

  const refreshRecent = async () => {
    try {
      const [p, m, a] = await Promise.all([
        api.get("/production?limit=5"),
        api.get("/materials?limit=5"),
        api.get("/attendance?limit=10"),
      ]);
      const combined = [
        ...p.data.map(x => ({ ...x, _t: "production" })),
        ...m.data.map(x => ({ ...x, _t: "material" })),
      ].sort((a, b) => (b.created_at || "").localeCompare(a.created_at || "")).slice(0, 5);
      setRecent(combined);
      const today = new Date().toISOString().slice(0, 10);
      const mine = a.data.find(x => x.worker_id === user.id && x.date === today);
      setAttToday(mine || null);
    } catch (e) {
      console.warn("Failed to refresh worker home data", e);
    }
  };

  const startVoice = () => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) {
      toast.error("Voice input needs Chrome/Edge browser on Android or desktop. Please type instead.");
      return;
    }
    if (!window.isSecureContext) {
      toast.error("Voice needs HTTPS. Please open the site over https://");
      return;
    }
    const tryLang = async (lang, fallback) => {
      const r = new SR();
      r.lang = lang;
      r.interimResults = false;
      r.maxAlternatives = 1;
      r.continuous = false;
      r.onstart = () => setListening(true);
      r.onend = () => setListening(false);
      r.onerror = (ev) => {
        setListening(false);
        console.warn("SpeechRecognition error", ev.error, ev);
        if (ev.error === "not-allowed" || ev.error === "service-not-allowed") {
          toast.error("Microphone permission blocked. Enable it in browser settings and reload.");
        } else if (ev.error === "no-speech") {
          toast.error("Didn't catch anything. Try again slowly.");
        } else if (ev.error === "language-not-supported" && fallback) {
          toast.info("Switching to English recognition…");
          tryLang(fallback, null);
        } else if (ev.error === "network") {
          toast.error("Voice needs internet. Check your connection.");
        } else {
          toast.error(`Voice error: ${ev.error}. Please type instead.`);
        }
      };
      r.onresult = (ev) => {
        const t = ev.results[0][0].transcript;
        setText((prev) => (prev ? prev + " " + t : t));
      };
      recogRef.current = r;
      try {
        r.start();
      } catch (e) {
        setListening(false);
        toast.error("Could not start microphone. Please retry.");
      }
    };
    tryLang("hi-IN", "en-IN");
  };

  const stopVoice = () => { recogRef.current?.stop(); setListening(false); };

  const doParse = async () => {
    if (!text.trim()) { toast.error("Please speak or type something first"); return; }
    setBusy(true); setParsed(null);
    try {
      const { data } = await api.post("/ai/parse", { text });
      if (!data.records || data.records.length === 0) {
        toast.error("Couldn't understand. Please rephrase.");
      } else {
        setParsed(data);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "AI parse failed");
    } finally { setBusy(false); }
  };

  const doCommit = async () => {
    if (!parsed) return;
    setBusy(true);
    try {
      const { data } = await api.post("/ai/commit", { records: parsed.records, raw_prompt: text });
      const ok = data.saved.filter(s => !s.error).length;
      toast.success(`Saved ${ok} record${ok === 1 ? "" : "s"} · धन्यवाद!`);
      setText(""); setParsed(null); refreshRecent();
    } catch (e) {
      toast.error("Save failed");
    } finally { setBusy(false); }
  };

  const doCheckIn = async () => {
    try { await api.post("/attendance/checkin"); toast.success("Checked in / हाजिरी दर्ज"); refreshRecent(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const doCheckOut = async () => {
    try { await api.post("/attendance/checkout"); toast.success("Checked out"); refreshRecent(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="min-h-screen bg-[#F9F9F8] pb-24">
      {/* Header */}
      <header className="sticky top-0 z-20 bg-[#F9F9F8]/85 backdrop-blur-xl border-b border-[#D8D8D5]">
        <div className="max-w-2xl mx-auto px-5 py-4 flex items-center justify-between">
          <div>
            <div className="text-[10px] tracking-[0.3em] uppercase text-[#6E6E6A]">Sango Terrazzo · Worker</div>
            <div className="font-display text-xl tracking-tight font-black text-[#1C1C1A]">Namaste, {user?.name} 🙏</div>
          </div>
          <Button variant="ghost" size="icon" onClick={logout} data-testid="worker-logout" className="rounded-full">
            <LogOut className="w-5 h-5" />
          </Button>
        </div>
      </header>

      <div className="max-w-2xl mx-auto px-5 pt-8 space-y-8">
        {/* Attendance */}
        <Card className="p-5 border border-[#D8D8D5] shadow-none rounded-md rise">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-[10px] tracking-[0.3em] uppercase text-[#6E6E6A]">Today · आज</div>
              <div className="font-display text-lg font-black">
                {attToday ? (attToday.check_out ? "Shift complete" : "On duty") : "Not checked in"}
              </div>
              {attToday && (
                <div className="text-xs text-[#6E6E6A] mt-1">
                  In: {attToday.check_in || "—"} · Out: {attToday.check_out || "—"}
                </div>
              )}
            </div>
            {!attToday ? (
              <Button onClick={doCheckIn} data-testid="worker-checkin" className="h-12 rounded-full px-6 bg-[#5B7B61] hover:bg-[#4a6650] text-white">
                <CheckCircle2 className="w-5 h-5 mr-2" /> Check in
              </Button>
            ) : !attToday.check_out ? (
              <Button onClick={doCheckOut} data-testid="worker-checkout" className="h-12 rounded-full px-6 bg-[#1C1C1A] hover:bg-black text-white">
                <Clock className="w-5 h-5 mr-2" /> Check out
              </Button>
            ) : (
              <Badge className="bg-[#5B7B61]/10 text-[#5B7B61] border border-[#5B7B61]/30">Done ✓</Badge>
            )}
          </div>
        </Card>

        {/* Prompt entry */}
        <section className="rise">
          <div className="text-[10px] tracking-[0.3em] uppercase text-[#6E6E6A] mb-2">Report your work</div>
          <h2 className="font-display text-2xl sm:text-3xl tracking-tighter font-black">
            बोलो या लिखो — AI समझ जायेगा
          </h2>
          <p className="text-[#6E6E6A] text-sm mt-2 leading-relaxed">
            Example: "Aaj 20 floor tile banaya, 3 bori cement laga, genset 4 ghante chala."
          </p>

          <div className="mt-6 flex flex-col items-center">
            <button
              type="button"
              data-testid="worker-mic"
              onClick={listening ? stopVoice : startVoice}
              className={`w-28 h-28 rounded-full flex items-center justify-center bg-[#C85A42] text-white shadow-lg hover:scale-105 transition-transform ${listening ? "mic-pulse" : ""}`}
              aria-label="Tap to speak"
            >
              <Mic className="w-10 h-10" strokeWidth={2.5} />
            </button>
            <div className="text-sm font-medium mt-3 text-[#1C1C1A]">
              {listening ? "Listening… / सुन रहे हैं…" : "Tap to speak / बोलने के लिए दबाएं"}
            </div>
          </div>

          <Textarea
            data-testid="worker-prompt"
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder="Type instead / यहाँ टाइप करें…"
            className="mt-6 min-h-[120px] text-base rounded-md border-[#D8D8D5] bg-white focus-visible:ring-[#1C1C1A]"
          />

          <div className="mt-4 flex gap-3">
            <Button
              onClick={doParse} disabled={busy || !text.trim()}
              data-testid="worker-parse"
              className="flex-1 h-14 rounded-full bg-[#1C1C1A] hover:bg-black text-white text-base"
            >
              <Send className="w-5 h-5 mr-2" /> {busy && !parsed ? "Understanding…" : "Submit / भेजें"}
            </Button>
          </div>
        </section>

        {/* Parsed preview */}
        {parsed?.records?.length > 0 && (
          <section className="rise">
            <div className="text-[10px] tracking-[0.3em] uppercase text-[#6E6E6A] mb-2">AI understood</div>
            <div className="space-y-3">
              {parsed.records.map((r, i) => {
                const Icon = TYPE_ICON[r.type] || Factory;
                return (
                  <Card key={i} data-testid={`parsed-record-${i}`} className="p-4 border border-[#D8D8D5] shadow-none rounded-md flex gap-3">
                    <div className="w-10 h-10 shrink-0 rounded-full bg-[#E8E8E6] flex items-center justify-center">
                      <Icon className="w-5 h-5 text-[#1C1C1A]" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-[10px] tracking-[0.25em] uppercase text-[#6E6E6A]">{TYPE_LABEL[r.type] || r.type}</div>
                      <div className="text-base font-medium mt-0.5 text-[#1C1C1A]">{r.summary}</div>
                      <div className="text-xs text-[#6E6E6A] mt-1">
                        {Object.entries(r.data || {}).filter(([k]) => !["worker_id","worker_name","notes","date"].includes(k))
                          .map(([k, v]) => `${k}: ${v}`).join(" · ")}
                      </div>
                    </div>
                    <Badge variant="outline" className="self-start text-[10px]">
                      {Math.round((r.confidence || 0) * 100)}%
                    </Badge>
                  </Card>
                );
              })}
            </div>
            <div className="mt-4 flex gap-3">
              <Button variant="outline" onClick={() => setParsed(null)} data-testid="worker-reject" className="flex-1 h-12 rounded-full">
                Redo / फिर से
              </Button>
              <Button onClick={doCommit} disabled={busy} data-testid="worker-confirm" className="flex-1 h-12 rounded-full bg-[#5B7B61] hover:bg-[#4a6650] text-white">
                <CheckCircle2 className="w-5 h-5 mr-2" /> Confirm & Save
              </Button>
            </div>
          </section>
        )}

        {/* Recent */}
        {recent.length > 0 && (
          <section className="rise">
            <div className="text-[10px] tracking-[0.3em] uppercase text-[#6E6E6A] mb-3">Recent entries</div>
            <div className="space-y-2">
              {recent.map((r) => {
                const Icon = TYPE_ICON[r._t] || Factory;
                return (
                  <div key={r.id} className="p-3 border border-[#D8D8D5] bg-white rounded-md flex items-center gap-3">
                    <Icon className="w-4 h-4 text-[#6E6E6A]" />
                    <div className="flex-1 text-sm text-[#1C1C1A]">
                      {r._t === "production"
                        ? `${r.quantity} ${r.unit} · ${r.product_category}`
                        : `${r.quantity} ${r.unit} · ${r.material}`}
                    </div>
                    <div className="text-[10px] text-[#6E6E6A]">{r.date}</div>
                  </div>
                );
              })}
            </div>
          </section>
        )}
      </div>
    </div>
  );
}
